import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ITool {
    name: string;
    icon: string | {};
    title: string;
}

export interface ICustomService {
    name: string;
    domain_id?: string;
    authInfo?: {
        authUrl: string;
        remainDays: number;
        providerName: string;
        tokenFieldName: string;
    };
}

export interface IDomain {
    concierge_domain_id: string;
    name: string;
    icon_ind: string;
    icon_name: string;
    icon_data: string;
    title: string;
    description?: string;
    suggestedPrompts?: string[];
    customServices?: ICustomService[];
}

export interface MappedDomains {
    [key: string]: string[];
}

export interface IConciergeCustomEvents {
    owner_click?: boolean;
    certified_username_click?: boolean;
}

export interface conciergeSliceState {
    visible: boolean;
    closeChat: boolean;
    onlySearch: boolean;
    isAppPanel: boolean;
    isMobile: boolean;
    fromSearch: boolean;
    width: number;
    newChat: string | null;
    availableDomains: IDomain[];
    availableTools: ITool[];
    selectedTools: string[];
    selectedDomains: string[];
    defaultSelectedDomains: string[] | null | undefined;
    focusInput: boolean;
    customStyles: any;
    customEvents: IConciergeCustomEvents;
    messageDomains: { [messageUid: string]: string };
    authorizedTools: string[];
    newRequestMessage: string | null;
    prependContext: { text: string; showInChat: boolean } | null;
}

export const conciergeSlice = createSlice({
    name: 'concierge',
    initialState: {
        visible: false,
        closeChat: false,
        width: 420,
        newChat: null,
        onlySearch: false,
        fromSearch: false,
        availableTools: [],
        availableDomains: [],
        selectedTools: [],
        selectedDomains: [],
        defaultSelectedDomains: null,
        focusInput: false,
        customStyles: {},
        customEvents: {},
        isAppPanel: false,
        isMobile: false,
        messageDomains: {},
        authorizedTools: [],
        newRequestMessage: null,
        prependContext: null,
    } as conciergeSliceState,
    reducers: {
        show: (state) => {
            state.visible = true;
        },
        hide: (state) => {
            state.visible = false;
        },
        toggleVisibility: (state) => {
            state.visible = !state.visible;
        },
        setConciergeWidgetWidth: (state, action) => {
            state.width = action.payload;
        },
        startNewChat: (state, action) => {
            state.newChat = action.payload;
        },
        clearNewChat: (state) => {
            state.newChat = null;
        },
        sendNewRequestMessage: (state, action) => {
            state.newRequestMessage = action.payload;
        },
        clearNewRequestMessage: (state) => {
            state.newRequestMessage = null;
        },
        closeChat: (state, action) => {
            state.closeChat = action.payload;
        },
        setOnlySearch: (state, action) => {
            state.onlySearch = action.payload;
        },
        setAvailableDomains: (state, action) => {
            state.availableDomains = action.payload;
        },
        setAvailableTools: (state, action) => {
            state.availableTools = action.payload;
        },
        setSelectedDomains: (state, action) => {
            state.selectedDomains = action.payload;
            state.focusInput = !state.focusInput;
        },
        setFromSearch: (state, action) => {
            state.fromSearch = action.payload;
        },
        setDefaultSelectedDomains: (state, action) => {
            state.defaultSelectedDomains = action.payload;
        },
        setCustomStyles: (state, action) => {
            state.customStyles = action.payload;
        },
        setIsAppPanel: (state, action) => {
            state.isAppPanel = action.payload;
        },
        setConciergeCustomEvents: (state, action: PayloadAction<IConciergeCustomEvents>) => {
            state.customEvents = action.payload;
        },
        setIsMobile: (state, action) => {
            state.isMobile = action.payload;
        },
        setMessageDomain: (state, action) => {
            const { messageUid, domainName } = action.payload;
            state.messageDomains[messageUid] = domainName;
        },
        addAuthorizedTool: (state, action) => {
            const toolName = action.payload;
            if (!state.authorizedTools.includes(toolName)) {
                state.authorizedTools.push(toolName);
            }
        },
        setPrependContext: (state, action) => {
            state.prependContext = action.payload;
        },
        clearPrependContext: (state) => {
            state.prependContext = null;
        },
    },
});

export const {
    show,
    hide,
    toggleVisibility,
    setConciergeWidgetWidth,
    startNewChat,
    clearNewChat,
    closeChat,
    setOnlySearch,
    setAvailableDomains,
    setSelectedDomains,
    setAvailableTools,
    setFromSearch,
    setDefaultSelectedDomains,
    setCustomStyles,
    setIsAppPanel,
    setIsMobile,
    setMessageDomain,
    addAuthorizedTool,
    sendNewRequestMessage,
    clearNewRequestMessage,
    setPrependContext,
    clearPrependContext,
    setConciergeCustomEvents,
} = conciergeSlice.actions;

export default conciergeSlice.reducer;
